import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import confetti from "canvas-confetti";
import { Link, useNavigate } from "react-router-dom";

const API_URL =
  "https://api.practicalinfosec.com/api";

const COUNTRY_CODES = [
  { code: "+91", name: "India" },
  { code: "+1", name: "United States / Canada" },
  { code: "+44", name: "United Kingdom" },
  { code: "+971", name: "United Arab Emirates" },
  { code: "+966", name: "Saudi Arabia" },
  { code: "+974", name: "Qatar" },
  { code: "+965", name: "Kuwait" },
  { code: "+968", name: "Oman" },
  { code: "+65", name: "Singapore" },
  { code: "+60", name: "Malaysia" },
  { code: "+94", name: "Sri Lanka" },
  { code: "+977", name: "Nepal" },
  { code: "+880", name: "Bangladesh" },
  { code: "+61", name: "Australia" },
  { code: "+49", name: "Germany" },
  { code: "+33", name: "France" },
];

const EXPERIENCE_OPTIONS = Array.from({ length: 11 }, (_, index) => index);

const GRACE_DAYS = 4;

function onlyDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function validateEmail(value) {
  return /^\S+@\S+\.\S+$/.test(String(value || "").trim());
}

function toIsoDate(value) {
  if (!value && value !== 0) return null;

  const stringValue = String(value).trim();

  if (/^\d{2}-\d{2}-\d{4}$/.test(stringValue)) {
    const [dd, mm, yyyy] = stringValue.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  const parsed = dayjs(stringValue);
  return parsed.isValid() ? parsed.toISOString() : null;
}

function normalizeBatchDay(value) {
  if (!value && value !== 0) return "";

  let normalized = String(value).trim().toUpperCase();
  normalized = normalized.replace(/[^A-Z]/g, "");

  if (normalized.includes("MORN") || normalized === "M" || normalized === "AM") {
    return "MORNING";
  }

  if (normalized.includes("EVE") || normalized === "E" || normalized === "PM") {
    return "EVENING";
  }

  return normalized;
}

function JuniorPenetrationTesterDemo() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("student");
  const [batchDayFilter, setBatchDayFilter] = useState("MORNING");
  const [batches, setBatches] = useState([]);
  const [selectedBatchKey, setSelectedBatchKey] = useState("");
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchesError, setBatchesError] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    countryCode: "+91",
    phone: "",
    collegeName: "",
    passedOutYear: "",
    companyName: "",
    yearsOfExperience: "",
    requestCallback: "NO",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Junior Penetration Tester Demo | Practical Infosec";
    loadDemoBatches();
  }, []);

  async function loadDemoBatches(months = 3) {
    setLoadingBatches(true);
    setBatchesError("");

    try {
      const response = await axios.get(`${API_URL}/demolinks/all-demolinks`);
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];

      const wantedMonthKeys = [];
      const now = dayjs();

      for (let index = 0; index < months; index += 1) {
        wantedMonthKeys.push(now.add(index, "month").format("YYYY-MM"));
      }

      const monthMap = new Map();

      for (const row of rows) {
        const groupingIso = toIsoDate(row.BatchStartDate ?? row.DemoDate);
        if (!groupingIso) continue;

        const groupingDate = dayjs(groupingIso);
        if (!groupingDate.isValid()) continue;

        const key = groupingDate.format("YYYY-MM");

        if (groupingDate.isSame(now, "month")) {
          const batchStartIso = toIsoDate(row.BatchStartDate);
          if (batchStartIso) {
            const batchStart = dayjs(batchStartIso);
            const graceEnd = batchStart.add(GRACE_DAYS, "day");

            if (now.isAfter(graceEnd)) continue;
          }
        }

        const demoIso = toIsoDate(row.DemoDate);
        const batchStartIso = toIsoDate(
          row.BatchStartDate ?? demoIso ?? groupingIso
        );

        const normalized = {
          ...row,
          __groupIso: groupingIso,
          __groupDayjs: groupingDate,
          __batchStartIso: batchStartIso,
          __demoDateIso: demoIso,
          __batchDayNorm: normalizeBatchDay(
            row.BatchDay ?? row.batchDay ?? row.Day ?? ""
          ),
          __timeRaw: String(row.BatchTime ?? row.Time ?? ""),
        };

        if (!monthMap.has(key)) {
          monthMap.set(key, []);
        }

        monthMap.get(key).push(normalized);
      }

      const computedBatches = wantedMonthKeys
        .map((key) => {
          const monthRows = monthMap.get(key) || [];

          if (!monthRows.length) return null;

          monthRows.sort((a, b) =>
            a.__groupDayjs.isBefore(b.__groupDayjs) ? -1 : 1
          );

          const representative = monthRows[0];

          return {
            key,
            label: dayjs(representative.__batchStartIso).isValid()
              ? dayjs(representative.__batchStartIso).format("MMM YYYY")
              : key,
            time: representative.__timeRaw || "TBD",
            batchStartIso: representative.__batchStartIso,
            batchDay: representative.__batchDayNorm || "",
            representativeId: representative.id ?? null,
            rawRows: monthRows,
          };
        })
        .filter(Boolean);

      setBatches(computedBatches);

      setTimeout(() => {
        const firstVisible = computedBatches.find((batch) =>
          batch.rawRows?.some(
            (row) => normalizeBatchDay(row.__batchDayNorm) === batchDayFilter
          )
        );

        if (firstVisible) {
          setSelectedBatchKey(firstVisible.key);
        }
      }, 100);
    } catch (error) {
      console.error("Demo batch load failed:", error);
      setBatchesError(
        error.response?.data?.message ||
          error.message ||
          "Failed to load demo batches"
      );
    } finally {
      setLoadingBatches(false);
    }
  }

  const displayedBatches = useMemo(() => {
    return batches
      .map((batch) => {
        const matchedRow = batch.rawRows?.find((row) => {
          const value = normalizeBatchDay(
            row.__batchDayNorm ?? row.BatchDay ?? row.batchDay ?? row.Day ?? ""
          );

          return value === batchDayFilter;
        });

        if (!matchedRow) return null;

        return {
          key: batch.key,
          label: batch.label,
          time: matchedRow.__timeRaw || batch.time || "TBD",
          batchStartIso: matchedRow.__batchStartIso || batch.batchStartIso,
          demoDateIso: matchedRow.__demoDateIso || null,
          batchDay:
            normalizeBatchDay(
              matchedRow.__batchDayNorm ??
                matchedRow.BatchDay ??
                matchedRow.batchDay ??
                ""
            ) ||
            batch.batchDay ||
            "",
          representativeId: matchedRow.id ?? batch.representativeId ?? null,
          rawRows: batch.rawRows,
        };
      })
      .filter(Boolean);
  }, [batches, batchDayFilter]);

  const selectedBatch = displayedBatches.find(
    (batch) => batch.key === selectedBatchKey
  );

  function updateField(field, value) {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  }

  function handleTabChange(tab) {
    setActiveTab(tab);

    setFormData((prev) => ({
      ...prev,
      collegeName: tab === "student" ? prev.collegeName : "",
      passedOutYear: tab === "student" ? prev.passedOutYear : "",
      companyName: tab === "professional" ? prev.companyName : "",
      yearsOfExperience:
        tab === "professional" ? prev.yearsOfExperience : "",
    }));

    setErrors({});
  }

  function validateForm() {
    const nextErrors = {};

    if (!formData.fullName.trim() || formData.fullName.trim().length < 2) {
      nextErrors.fullName = "Please enter a valid full name.";
    }

    if (!validateEmail(formData.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    const phoneDigits = onlyDigits(formData.phone);

    if (!phoneDigits) {
      nextErrors.phone = "Please enter phone number.";
    } else if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      nextErrors.phone = "Phone number must be 7 to 15 digits.";
    }

    if (activeTab === "student") {
      if (!formData.collegeName.trim()) {
        nextErrors.collegeName = "Please enter college name.";
      }

      if (!/^[0-9]{4}$/.test(formData.passedOutYear.trim())) {
        nextErrors.passedOutYear = "Please enter a valid 4-digit year.";
      }
    }

    if (activeTab === "professional") {
      if (!formData.companyName.trim()) {
        nextErrors.companyName = "Please enter company name.";
      }

      if (formData.yearsOfExperience === "") {
        nextErrors.yearsOfExperience = "Please select years of experience.";
      }
    }

    if (!selectedBatchKey || !selectedBatch) {
      nextErrors.batch = "Please select a batch.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function resetForm() {
    setFormData({
      fullName: "",
      email: "",
      countryCode: "+91",
      phone: "",
      collegeName: "",
      passedOutYear: "",
      companyName: "",
      yearsOfExperience: "",
      requestCallback: "NO",
    });

    setErrors({});
    setSelectedBatchKey(displayedBatches[0]?.key || "");
    setActiveTab("student");
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Check the form",
        text: "Please fix the highlighted errors and try again.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const payload = {
      FullName: formData.fullName.trim(),
      Email: formData.email.trim(),
      PhoneNumber: `${formData.countryCode} ${onlyDigits(formData.phone)}`,
      LeadCategory: activeTab,
      LeadSource: "ORGANIC",
      EnrollmentType: "JUNIOR PENETRATION TESTER",
      ...(activeTab === "student"
        ? {
            CollegeName: formData.collegeName.trim(),
            PassedOutYear: formData.passedOutYear.trim(),
          }
        : {
            YearsOfExperience: formData.yearsOfExperience,
            CompanyName: formData.companyName.trim(),
          }),
      AvailabilityBatch: selectedBatch?.batchStartIso || null,
      BatchDay: selectedBatch?.batchDay || "",
      CallBack: formData.requestCallback || "NO",
    };

    const endpoint =
      activeTab === "student"
        ? `${API_URL}/leads/form-student`
        : `${API_URL}/leads/form-professional`;

    Swal.fire({
      title: "Please wait...",
      html: "Submitting your demo registration...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await axios.post(endpoint, payload, {
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
        },
      });

      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
      });

      Swal.close();

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "demo_registration_submit",
        course: "JUNIOR PENETRATION TESTER",
        lead_category: activeTab,
      });

      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }

      navigate("/thank-you-junior");
    } catch (error) {
      console.error("Demo registration failed:", error);

      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error.response?.data?.message ||
          error.message ||
          "Unable to submit registration. Please try again.",
        confirmButtonColor: "#dc2626",
      });
    }
  }

  return (
    <main className="min-h-screen overflow-hidden bg-slate-950">
      {/* HERO */}
      <section className="relative px-4 py-10 overflow-hidden md:px-8 lg:px-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.35),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.22),transparent_36%),radial-gradient(circle_at_center,rgba(124,58,237,0.16),transparent_46%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>

        <div className="relative z-10 mx-auto max-w-[1600px]">
          <div className="grid items-start gap-8 lg:grid-cols-12">
            {/* LEFT VIDEO */}
            <section className="lg:col-span-8">
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  AppSec / VAPT Demo Class
                </span>

                <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-6xl">
                  Junior Penetration Tester{" "}
                  <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                    Demo Registration
                  </span>
                </h1>

                <p className="max-w-4xl mt-5 text-lg font-semibold leading-8 text-slate-300">
                  Watch the training demo and register for the upcoming AppSec /
                  VAPT Junior Penetration Tester batch. Learn practical web
                  security, OWASP, recon, tools, and real assessment workflows.
                </p>
              </div>

              <div className="overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur">
                <div className="flex items-center justify-between gap-4 px-5 py-4 border-b bg-slate-900/95 border-white/10">
                  <div>
                    <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                      Training Demo Recording
                    </p>
                    <h2 className="text-xl font-black text-white">
                      Appsec/VAPT - Training-Demo Meeting Recording
                    </h2>
                  </div>

                  <div className="hidden px-4 py-2 text-xs font-black text-green-300 border rounded-full md:inline-flex bg-green-400/10 border-green-400/20">
                    Free Demo
                  </div>
                </div>

                <div className="w-full aspect-video lg:h-[600px] bg-black">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/utavxY-EaDU?rel=0&modestbranding=1"
                    title="Junior Penetration Tester Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-3">
                {[
                  ["Live Classes", "Instructor-led practical training"],
                  ["Hands-on Labs", "Real AppSec/VAPT practice"],
                  ["Career Support", "Mock interviews and roadmap"],
                ].map(([title, description]) => (
                  <div
                    key={title}
                    className="p-5 border rounded-2xl bg-white/10 border-white/15"
                  >
                    <h3 className="text-lg font-black text-white">{title}</h3>
                    <p className="mt-2 text-sm font-semibold text-slate-300">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* RIGHT FORM */}
            <aside className="lg:col-span-4">
              <form
                onSubmit={handleSubmit}
                className="sticky top-6 overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white"
              >
                <div className="px-6 py-6 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950">
                  <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                    Register Now
                  </p>

                  <h2 className="mt-2 text-3xl font-black text-white">
                    Secure Your Demo Seat
                  </h2>

                  <p className="mt-2 text-sm font-semibold leading-6 text-blue-100">
                    Fill your details and choose your preferred upcoming batch.
                  </p>
                </div>

                <div className="max-h-[calc(100vh-160px)] overflow-y-auto p-5 space-y-5">
                  {/* Tabs */}
                  <div className="grid grid-cols-2 gap-3 p-1 bg-slate-100 rounded-2xl">
                    <button
                      type="button"
                      onClick={() => handleTabChange("student")}
                      className={`px-4 py-3 text-sm font-black rounded-xl transition ${
                        activeTab === "student"
                          ? "bg-blue-700 text-white shadow-lg"
                          : "text-slate-700 hover:bg-white"
                      }`}
                    >
                      Student
                    </button>

                    <button
                      type="button"
                      onClick={() => handleTabChange("professional")}
                      className={`px-4 py-3 text-sm font-black rounded-xl transition ${
                        activeTab === "professional"
                          ? "bg-blue-700 text-white shadow-lg"
                          : "text-slate-700 hover:bg-white"
                      }`}
                    >
                      Professional
                    </button>
                  </div>

                  {/* Basic fields */}
                  <div className="grid grid-cols-1 gap-4">
                    <InputField
                      label="Full Name"
                      value={formData.fullName}
                      error={errors.fullName}
                      placeholder="Jane Doe"
                      onChange={(value) => updateField("fullName", value)}
                    />

                    <InputField
                      label="Email"
                      value={formData.email}
                      error={errors.email}
                      placeholder="you@example.com"
                      onChange={(value) => updateField("email", value)}
                    />

                    <div>
                      <label className="block mb-2 text-sm font-black text-slate-800">
                        Phone Number
                      </label>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[150px_1fr]">
                        <select
                          value={formData.countryCode}
                          onChange={(event) =>
                            updateField("countryCode", event.target.value)
                          }
                          className="w-full px-3 py-3 text-sm font-bold bg-white border shadow-sm outline-none rounded-xl border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        >
                          {COUNTRY_CODES.map((country) => (
                            <option
                              key={`${country.name}-${country.code}`}
                              value={country.code}
                            >
                              {country.name} ({country.code})
                            </option>
                          ))}
                        </select>

                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(event) =>
                            updateField("phone", event.target.value)
                          }
                          placeholder="Mobile number"
                          className={`w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
                            errors.phone
                              ? "border-red-500"
                              : onlyDigits(formData.phone).length >= 7
                              ? "border-green-500"
                              : "border-slate-200 focus:border-blue-500"
                          }`}
                        />
                      </div>

                      {errors.phone && (
                        <p className="mt-1 text-xs font-semibold text-red-600">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Student / Professional */}
                  {activeTab === "student" ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <InputField
                        label="College Name"
                        value={formData.collegeName}
                        error={errors.collegeName}
                        placeholder="College / University"
                        onChange={(value) => updateField("collegeName", value)}
                      />

                      <InputField
                        label="Passed Out / Pursuing Year"
                        value={formData.passedOutYear}
                        error={errors.passedOutYear}
                        placeholder="e.g. 2026"
                        maxLength={4}
                        onChange={(value) =>
                          updateField("passedOutYear", onlyDigits(value))
                        }
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <InputField
                        label="Company Name"
                        value={formData.companyName}
                        error={errors.companyName}
                        placeholder="Enter Company Name"
                        onChange={(value) => updateField("companyName", value)}
                      />

                      <div>
                        <label className="block mb-2 text-sm font-black text-slate-800">
                          Years of Experience
                        </label>

                        <select
                          value={formData.yearsOfExperience}
                          onChange={(event) =>
                            updateField("yearsOfExperience", event.target.value)
                          }
                          className={`w-full px-4 py-3 text-sm font-semibold bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
                            errors.yearsOfExperience
                              ? "border-red-500"
                              : formData.yearsOfExperience !== ""
                              ? "border-green-500"
                              : "border-slate-200 focus:border-blue-500"
                          }`}
                        >
                          <option value="">Select years</option>
                          {EXPERIENCE_OPTIONS.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>

                        {errors.yearsOfExperience && (
                          <p className="mt-1 text-xs font-semibold text-red-600">
                            {errors.yearsOfExperience}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Batch filter */}
                  <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <label className="text-sm font-black text-slate-900">
                        Batch Day
                      </label>

                      <select
                        value={batchDayFilter}
                        onChange={(event) => {
                          setBatchDayFilter(event.target.value);
                          setSelectedBatchKey("");
                        }}
                        className="px-4 py-2 text-sm font-black bg-white border rounded-xl border-slate-200 focus:border-blue-500"
                      >
                        <option value="MORNING">MORNING</option>
                        <option value="EVENING">EVENING</option>
                      </select>
                    </div>
                  </div>

                  {/* Batches */}
                  <div>
                    <label className="block mb-3 text-sm font-black text-slate-800">
                      Select Batch
                    </label>

                    {loadingBatches && (
                      <div className="p-4 text-sm font-bold text-center border rounded-2xl bg-slate-50 border-slate-200 text-slate-600">
                        Loading batches...
                      </div>
                    )}

                    {batchesError && (
                      <div className="p-4 text-sm font-bold text-red-700 border border-red-100 rounded-2xl bg-red-50">
                        Failed to load batches: {batchesError}
                      </div>
                    )}

                    {!loadingBatches &&
                      !batchesError &&
                      displayedBatches.length === 0 && (
                        <div className="p-4 text-sm font-bold text-center border rounded-2xl bg-slate-50 border-slate-200 text-slate-600">
                          No demo batches available for the selected filter.
                        </div>
                      )}

                    <div className="grid grid-cols-1 gap-3">
                      {displayedBatches.map((batch) => {
                        const selected = selectedBatchKey === batch.key;

                        return (
                          <button
                            key={batch.key}
                            type="button"
                            onClick={() => setSelectedBatchKey(batch.key)}
                            className={`p-4 text-left transition border rounded-2xl ${
                              selected
                                ? "border-blue-600 bg-blue-50 shadow-lg ring-4 ring-blue-100"
                                : "border-slate-200 bg-white hover:border-blue-300 hover:shadow-md"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <h3 className="text-lg font-black text-slate-900">
                                {batch.label}
                              </h3>

                              {selected && (
                                <span className="px-3 py-1 text-xs font-black text-white bg-blue-700 rounded-full">
                                  Selected
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-1 gap-2 mt-3 text-sm font-bold text-slate-700">
                              <p>
                                Batch Start Date:{" "}
                                <span className="text-green-700">
                                  {batch.batchStartIso
                                    ? dayjs(batch.batchStartIso).format(
                                        "DD MMM YYYY"
                                      )
                                    : "TBD"}
                                </span>
                              </p>

                              <p>
                                Batch Day:{" "}
                                <span
                                  className={
                                    batch.batchDay === "MORNING"
                                      ? "text-amber-700"
                                      : "text-red-700"
                                  }
                                >
                                  {batch.batchDay || "-"}
                                </span>
                              </p>

                              <p>
                                Time:{" "}
                                <span className="text-blue-800">
                                  {batch.time || "TBD"}
                                </span>
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {errors.batch && (
                      <p className="mt-1 text-xs font-semibold text-red-600">
                        {errors.batch}
                      </p>
                    )}
                  </div>

                  {selectedBatchKey && (
                    <div className="p-4 border border-blue-100 rounded-2xl bg-blue-50">
                      <p className="mb-3 text-sm font-black text-slate-900">
                        Get a Call Back From Our Experts
                      </p>

                      <div className="flex gap-5">
                        <label className="flex items-center gap-2 text-sm font-bold">
                          <input
                            type="radio"
                            name="requestCallback"
                            value="YES"
                            checked={formData.requestCallback === "YES"}
                            onChange={() =>
                              updateField("requestCallback", "YES")
                            }
                          />
                          YES
                        </label>

                        <label className="flex items-center gap-2 text-sm font-bold">
                          <input
                            type="radio"
                            name="requestCallback"
                            value="NO"
                            checked={formData.requestCallback === "NO"}
                            onChange={() => updateField("requestCallback", "NO")}
                          />
                          NO
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-4 text-base font-black text-white transition rounded-xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-[1.02] shadow-lg"
                    >
                      Submit Registration
                    </button>

                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-4 text-base font-black text-white transition rounded-xl bg-slate-900 hover:bg-slate-700"
                    >
                      Reset
                    </button>
                  </div>

                  <p className="text-xs font-semibold leading-5 text-center text-slate-500">
                    By submitting, you agree to be contacted by Practical Infosec
                    regarding course and batch details.
                  </p>
                </div>
              </form>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}

function InputField({
  label,
  value,
  onChange,
  placeholder,
  error,
  maxLength,
}) {
  return (
    <div>
      <label className="block mb-2 text-sm font-black text-slate-800">
        {label}
      </label>

      <input
        type="text"
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
          error
            ? "border-red-500"
            : String(value || "").trim()
            ? "border-green-500"
            : "border-slate-200 focus:border-blue-500"
        }`}
      />

      {error && (
        <p className="mt-1 text-xs font-semibold text-red-600">{error}</p>
      )}
    </div>
  );
}

export default JuniorPenetrationTesterDemo;